/**
 * ResourcesTab Component (T013, T068 - US1/US5)
 * Dedicated Resources tab with list view and settings sub-navigation
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Settings } from 'lucide-react';
import { Card } from '@/shared/components/ui/card';
import { ResourceTable } from './ResourceTable';
import { ResourceFilterPanel } from './ResourceFilterPanel';
import { PaginationControls } from './PaginationControls';
import { ResourceSettingsTab } from './ResourceSettingsTab';
import {
  useAdminResources,
  useDeleteResource,
  useResourceTypes,
  useResourceCategories,
} from '../hooks/useAdminResources';
import { usePagination } from '../hooks/usePagination';
import type {
  ResourceListParams,
  AdminResource,
  ResourceSortField,
  SortDirection,
} from '../types/admin-resource.type';

type SubView = 'list' | 'settings';

export function ResourcesTab() {
  const navigate = useNavigate();
  const [subView, setSubView] = useState<SubView>('list');
  const [deleteConfirm, setDeleteConfirm] = useState<AdminResource | null>(null);

  const { page, pageSize, setPage, setPageSize } = usePagination({ initialPageSize: 25 });
  const [sortBy, setSortBy] = useState<ResourceSortField>('updatedAt');
  const [sortOrder, setSortOrder] = useState<SortDirection>('desc');
  const [filters, setFilters] = useState<Omit<ResourceListParams, 'page' | 'pageSize' | 'sortBy' | 'sortOrder'>>({
    search: '',
    status: undefined,
    typeId: '',
    categoryId: '',
    isFeatured: undefined,
  });

  const queryParams: ResourceListParams = { page, pageSize, sortBy, sortOrder, ...filters };

  const { data, isLoading } = useAdminResources(queryParams);
  const resources = data?.items || [];
  const meta = data?.meta;
  const totalCount = meta?.total || 0;

  const { data: resourceTypes = [] } = useResourceTypes();
  const { data: resourceCategories = [] } = useResourceCategories();

  const deleteMutation = useDeleteResource();

  const isFiltered = !!(
    filters.search ||
    filters.status ||
    filters.typeId ||
    filters.categoryId ||
    filters.isFeatured !== undefined
  );

  const handleSort = (field: string) => {
    const sortField = field as ResourceSortField;
    if (sortBy === sortField) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(sortField);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handleCreate = () => {
    navigate('/admin/resources/create');
  };

  const handleEdit = (resourceId: string) => {
    navigate(`/admin/resources/${resourceId}/edit`);
  };

  const handleDelete = (resourceId: string) => {
    const resource = resources.find((r) => r.id === resourceId);
    if (resource) {
      setDeleteConfirm(resource);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteMutation.mutateAsync(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete resource:', error);
    }
  };

  if (subView === 'settings') {
    return (
      <div data-testid="resources-tab" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Resources</h2>
            <p className="text-gray-600 mt-1">
              Manage articles, guides, and educational resources
            </p>
          </div>
        </div>
        <div className="flex gap-2 border-b border-gray-200 pb-2">
          <button
            type="button"
            onClick={() => setSubView('list')}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            All Resources
          </button>
          <button
            type="button"
            onClick={() => setSubView('settings')}
            className="px-4 py-2 text-sm font-medium text-[#026857] border-b-2 border-[#026857] transition-colors"
          >
            Resources Settings
          </button>
        </div>
        <ResourceSettingsTab />
      </div>
    );
  }

  return (
    <div data-testid="resources-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Resources</h2>
          <p className="text-gray-600 mt-1">
            Manage articles, guides, and educational resources
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSubView('settings')}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            aria-label="Resources Settings"
          >
            <Settings className="h-4 w-4" />
            Settings
          </button>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-[#026857] text-white rounded-md hover:bg-[#024d43] transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Resource
          </button>
        </div>
      </div>

      <Card>
        <ResourceFilterPanel
          searchValue={filters.search}
          onSearchChange={(search) => setFilters({ ...filters, search })}
          statusValue={filters.status}
          onStatusChange={(status) => setFilters({ ...filters, status: status || undefined })}
          typeValue={filters.typeId}
          onTypeChange={(typeId) => setFilters({ ...filters, typeId })}
          categoryValue={filters.categoryId}
          onCategoryChange={(categoryId) => setFilters({ ...filters, categoryId })}
          isFeaturedValue={filters.isFeatured}
          onIsFeaturedChange={(isFeatured) => setFilters({ ...filters, isFeatured })}
          typeOptions={resourceTypes.map((t) => ({ value: t.id, label: t.name }))}
          categoryOptions={resourceCategories.map((c) => ({ value: c.id, label: c.name }))}
        />
      </Card>

      <Card>
        <ResourceTable
          resources={resources}
          totalCount={totalCount}
          isFiltered={isFiltered}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isLoading={isLoading}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      </Card>

      {totalCount > 0 && meta && (
        <div className="flex justify-center">
          <PaginationControls
            meta={meta}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[10, 25, 50]}
          />
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Resource</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete{' '}
              <span className="font-medium">{deleteConfirm.title}</span>?
              This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleteMutation.isPending ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Delete Resource</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
